<template>
  <div class="ncrm-mailjet-property-mappings">
    <!-- Header: refresh + save -->
    <div class="d-flex flex-wrap align-center justify-space-between mb-3 ga-2">
      <div>
        <div class="text-subtitle-2">
          {{ $t("ncrm.integrations.mailjet.propertyMapping.title") }}
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ $t("ncrm.integrations.mailjet.propertyMapping.subtitle") }}
        </div>
      </div>
      <div class="d-flex align-center ga-2">
        <v-btn
          variant="text"
          size="small"
          :loading="refreshing"
          :disabled="saving"
          @click="refreshFromMailjet"
        >
          <v-icon start size="16">mdi-cloud-refresh-outline</v-icon>
          {{ $t("ncrm.integrations.mailjet.propertyMapping.refresh") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          size="small"
          :loading="saving"
          :disabled="!hasChanges"
          @click="saveMappings"
        >
          {{ $t("ncrm.integrations.mailjet.propertyMapping.save") }}
        </v-btn>
      </div>
    </div>

    <!-- Loading -->
    <v-skeleton-loader v-if="loading && !mappings.length" type="table-row@5" />

    <!-- Empty: never refreshed yet -->
    <v-alert v-else-if="!mappings.length" type="info">
      {{ $t("ncrm.integrations.mailjet.propertyMapping.empty") }}
    </v-alert>

    <!-- Table -->
    <v-data-table
      v-else
      :headers="headers"
      :items="mappings"
      :items-per-page="-1"
      density="comfortable"
      class="elevation-0"
      item-key="mailjet_property"
      hide-default-footer
    >
      <template #item.mailjet_property="{ item }">
        <div class="d-flex align-center ga-2">
          <span class="ncrm-text-strong">{{ item.mailjet_property }}</span>
          <v-chip
            v-if="item.is_builtin"
            size="x-small"
            color="primary"
          >
            {{ $t("ncrm.integrations.mailjet.propertyMapping.builtin") }}
          </v-chip>
        </div>
      </template>
      <template #item.mailjet_property_type="{ item }">
        <span class="text-caption text-medium-emphasis">
          {{ item.mailjet_property_type || "—" }}
        </span>
      </template>
      <template #item.crm_source_field="{ item }">
        <v-select
          :model-value="draftMapping[item.mailjet_property] ?? null"
          :items="crmSourceItems"
          item-title="label"
          item-value="value"
          :placeholder="$t('ncrm.integrations.mailjet.propertyMapping.pickSource')"
          density="compact"
          hide-details
          clearable
          style="max-width: 320px;"
          @update:model-value="onPickSource(item.mailjet_property, $event)"
        />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "NcrmMailjetPropertyMappingsTab",
  props: {
    accountId: { type: [Number, String], required: true },
  },
  emits: ["notify"],
  data() {
    return {
      mappings: [],
      crmSources: { person: [], company: [] },
      // Live edit state — keyed by mailjet_property -> crm_source_field (or null).
      // Compared against `originalMapping` to compute `hasChanges`.
      draftMapping: {},
      originalMapping: {},
      loading: false,
      refreshing: false,
      saving: false,
    };
  },
  computed: {
    headers() {
      return [
        {
          title: this.$t("ncrm.integrations.mailjet.propertyMapping.columns.mailjet"),
          key: "mailjet_property",
          sortable: false,
        },
        {
          title: this.$t("ncrm.integrations.mailjet.propertyMapping.columns.type"),
          key: "mailjet_property_type",
          sortable: false,
          width: 100,
        },
        {
          title: this.$t("ncrm.integrations.mailjet.propertyMapping.columns.crmSource"),
          key: "crm_source_field",
          sortable: false,
        },
      ];
    },
    // Flat array of selectable CRM fields plus disabled group labels.
    crmSourceItems() {
      const items = [];
      if (Array.isArray(this.crmSources.person) && this.crmSources.person.length) {
        items.push(this.crmSourceGroupItem("person"));
        for (const field of this.crmSources.person) {
          items.push({ label: `person.${field}`, value: `person.${field}` });
        }
      }
      if (Array.isArray(this.crmSources.company) && this.crmSources.company.length) {
        items.push(this.crmSourceGroupItem("company"));
        for (const field of this.crmSources.company) {
          items.push({ label: `company.${field}`, value: `company.${field}` });
        }
      }
      return items;
    },
    crmSourceValues() {
      return new Set(
        this.crmSourceItems
          .filter((item) => !item?.props?.disabled && item?.value)
          .map((item) => item.value)
      );
    },
    hasChanges() {
      for (const m of this.mappings) {
        const original = this.originalMapping[m.mailjet_property] ?? null;
        const current = this.draftMapping[m.mailjet_property] ?? null;
        if (original !== current) return true;
      }
      return false;
    },
  },
  watch: {
    accountId: {
      handler() {
        this.fetchMappings();
      },
      immediate: true,
    },
  },
  methods: {
    extractError(error, fallbackKey) {
      return (
        error?.response?.data?.error ||
        error?.message ||
        this.$t(fallbackKey)
      );
    },
    crmSourceGroupItem(kind) {
      return {
        label: this.$t(`ncrm.integrations.mailjet.propertyMapping.sources.${kind}`),
        value: `__mailjet_source_group_${kind}`,
        props: {
          disabled: true,
          class: "ncrm-mailjet-property-mappings__source-group",
        },
      };
    },
    onPickSource(mailjetProperty, value) {
      if (value && !this.crmSourceValues.has(value)) return;
      this.draftMapping = { ...this.draftMapping, [mailjetProperty]: value || null };
    },
    applyServerData(data) {
      this.mappings = Array.isArray(data?.mappings) ? data.mappings : [];
      this.crmSources = data?.crm_sources || { person: [], company: [] };
      const snapshot = {};
      for (const row of this.mappings) {
        snapshot[row.mailjet_property] = row.crm_source_field || null;
      }
      this.draftMapping = { ...snapshot };
      this.originalMapping = { ...snapshot };
    },
    async fetchMappings() {
      if (!this.accountId) return;
      this.loading = true;
      try {
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/property-mappings`
        );
        this.applyServerData(data);
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message: this.extractError(error, "ncrm.integrations.mailjet.propertyMapping.errors.load"),
        });
      } finally {
        this.loading = false;
      }
    },
    async refreshFromMailjet() {
      if (!this.accountId) return;
      this.refreshing = true;
      try {
        const { data } = await axios.post(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/property-mappings/refresh`
        );
        this.applyServerData(data);
        this.$emit("notify", {
          color: "success",
          message: this.$t("ncrm.integrations.mailjet.propertyMapping.alerts.refreshed", {
            count: this.mappings.length,
          }),
        });
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message: this.extractError(error, "ncrm.integrations.mailjet.propertyMapping.errors.refresh"),
        });
      } finally {
        this.refreshing = false;
      }
    },
    async saveMappings() {
      if (!this.accountId || !this.hasChanges) return;
      this.saving = true;
      try {
        // Send ALL mappings (not just changed) so backend's validator runs
        // against the full set. This makes the request idempotent.
        const payload = this.mappings.map((m) => ({
          mailjet_property: m.mailjet_property,
          crm_source_field: this.draftMapping[m.mailjet_property] ?? null,
        }));
        const { data } = await axios.put(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/property-mappings`,
          { mappings: payload }
        );
        this.applyServerData(data);
        this.$emit("notify", {
          color: "success",
          message: this.$t("ncrm.integrations.mailjet.propertyMapping.alerts.saved"),
        });
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message: this.extractError(error, "ncrm.integrations.mailjet.propertyMapping.errors.save"),
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
